/**
 * Party-facts lever F (WP-7) — **the record panel's party selection**, shared by the two surfaces
 * that need it and owned by neither.
 *
 * `CrmPartiesField` (in the fields grid) offers *"show emails"* on a firm; `CrmRecordTimeline` (a
 * section further down the same scroll) renders the chip row and does the filtering. They are
 * siblings under `CrmRecordPanel`, so the state lives above both — a context rather than a prop
 * drilled through `renderValue`, `ProjectedFieldRow` and four layers of the panel.
 *
 * ⛔ **TRANSIENT VIEW STATE, NEVER A URL PARAM (D-11).** A filter inside a selection is not a
 * drill-down view: it does not survive a share, a Back, or a ‹ › hop to the next deal. That is the
 * navigation model's rule for the kind chips beside it, and the party row obeys the same one — which
 * is also why this file hand-rolls no history at all.
 *
 * **Host-scoped, and reset DURING RENDER**, the pattern `CrmRecordTimeline`'s `openBodies` and `win`
 * already use and for the same measured reason: an effect runs one render too late, so the previous
 * record's selection would paint once — and, worse, would be sent as a filter on the NEXT record's
 * first evidence fetch, i.e. a governed read for one deal narrowed by another deal's parties.
 *
 * The default context is INERT (`available: false`, no-op actions), so `CrmPartiesField` renders
 * standalone — in the grid, in a story, in a unit test — with its "show emails" affordance disabled,
 * exactly as PR-D2 shipped it. The affordance goes live only where a provider is mounted AND the
 * server sent a `party_filter` block, i.e. only where the flag is on.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface PartyFilterState {
  /** Whether anything can act on a selection here — a provider is mounted AND the feature is live. */
  available: boolean;
  /** The chosen node ids (`org:12` / `person:34`), in click order. Empty = no party selected. */
  selected: string[];
  toggle: (id: string) => void;
  /** "show emails" — REPLACES the selection with this one party and asks the timeline to scroll. */
  showOnly: (id: string) => void;
  clear: () => void;
  /** Bumped by `showOnly`; the timeline scrolls itself into view when it changes. A counter rather
   *  than a boolean, so two clicks on the same chip scroll twice. */
  scrollNonce: number;
}

const INERT: PartyFilterState = {
  available: false,
  selected: [],
  toggle: () => {},
  showOnly: () => {},
  clear: () => {},
  scrollNonce: 0,
};

const PartyFilterContext = createContext<PartyFilterState>(INERT);

/** One frozen empty array, so a host mismatch yields a STABLE reference. A `[]` literal in that
 * branch is a new array on every render, which would re-key the memo below — and, through it, the
 * evidence query's `party` argument — on every keystroke elsewhere in the panel. */
const NO_PARTIES: string[] = [];

export function useCrmPartyFilter(): PartyFilterState {
  return useContext(PartyFilterContext);
}

/** Mounted by `CrmRecordPanelContainer` around the panel, so the fields grid and the timeline
 * section share one selection. `entryId` is the host: a change to it clears the selection during
 * render, before any child can issue a fetch with it. */
export function CrmPartyFilterProvider({ entryId, children }:
                                       { entryId: number; children: React.ReactNode }) {
  const [state, setState] = useState<{ host: number; ids: string[]; nonce: number }>(
    { host: entryId, ids: [], nonce: 0 },
  );
  if (state.host !== entryId) setState({ host: entryId, ids: [], nonce: 0 });
  const ids = state.host === entryId ? state.ids : NO_PARTIES;

  const toggle = useCallback((id: string) => {
    setState((s) => {
      const current = s.host === entryId ? s.ids : NO_PARTIES;
      return {
        host: entryId, nonce: s.nonce,
        ids: current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
      };
    });
  }, [entryId]);

  const showOnly = useCallback((id: string) => {
    setState((s) => ({ host: entryId, ids: [id], nonce: s.nonce + 1 }));
  }, [entryId]);

  const clear = useCallback(() => {
    setState((s) => ({ host: entryId, ids: [], nonce: s.nonce }));
  }, [entryId]);

  const value = useMemo<PartyFilterState>(
    () => ({ available: true, selected: ids, toggle, showOnly, clear, scrollNonce: state.nonce }),
    [ids, toggle, showOnly, clear, state.nonce],
  );
  return <PartyFilterContext.Provider value={value}>{children}</PartyFilterContext.Provider>;
}

export default CrmPartyFilterProvider;
